import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Edit, Trash2, ToggleLeft, ToggleRight, User, Building } from 'lucide-react';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/alerts';

import { customerService } from '@/services/customerService';
import { Customer, CUSTOMER_TYPES } from '@/types/customer';
import { useAuth } from '@/context/AuthContext';
import AddClientModal from '@/components/AddClientModal';

const Customers: React.FC = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<{
    search: string;
    type: 'NATURAL' | 'JURIDICA' | '';
    active: string;
  }>({
    search: '',
    type: '',
    active: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Queries
  const { data: customersData, isLoading, error, refetch } = useQuery({
    queryKey: ['customers', page, limit, filters],
    queryFn: () => customerService.getCustomers({
      page,
      limit,
      search: filters.search || undefined,
      type: (filters.type as 'NATURAL' | 'JURIDICA') || undefined,
      active: filters.active || undefined
    }),
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds
  });

  // Mutations
  const deleteCustomerMutation = useMutation({
    mutationFn: (id: number) => customerService.deleteCustomer(id),
    onSuccess: () => {
      showSuccess('Cliente eliminado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al eliminar cliente');
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => customerService.toggleCustomerStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al cambiar estado del cliente');
    }
  });

  // Event handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    showDeleteConfirm(
      `¿Está seguro de eliminar el cliente "${customer.name}"?`,
      () => deleteCustomerMutation.mutate(customer.id)
    );
  };

  const handleToggleStatus = (customer: Customer) => {
    showDeleteConfirm(
      `¿Está seguro de ${customer.active ? 'desactivar' : 'activar'} el cliente "${customer.name}"?`,
      () => toggleStatusMutation.mutate(customer.id),
      customer.active ? 'Desactivar' : 'Activar',
      customer.active ? 'danger' : 'success'
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-ES');
    } catch (error) {
      return 'N/A';
    }
  };

  const getCustomerTypeIcon = (type: 'NATURAL' | 'JURIDICA') => {
    return type === 'NATURAL' ? <User className="h-4 w-4" /> : <Building className="h-4 w-4" />;
  };

  const canManage = hasPermission(['manage_customers']);
  const canCreate = hasPermission(['create_customers', 'manage_customers']);
  const canEdit = hasPermission(['edit_customers', 'manage_customers']);
  const canDelete = hasPermission(['delete_customers', 'manage_customers']);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Clientes</h1>
              <p className="mt-1 text-sm text-gray-600">
                Gestiona la información de tus clientes
              </p>
            </div>
            {canCreate && (
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setShowAddModal(true);
                }}
                className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus className="h-5 w-5" />
                <span className="sm:inline">Nuevo Cliente</span>
              </button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email, documento, NIT..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="btn-secondary flex items-center gap-2 flex-1 sm:flex-initial justify-center"
              >
                <Filter className="h-5 w-5" />
                <span>Filtros</span>
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 sm:flex-initial"
              >
                Buscar
              </button>
            </div>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">
                    Tipo de Cliente
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Todos los tipos</option>
                    {CUSTOMER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">
                    Estado
                  </label>
                  <select
                    value={filters.active?.toString() || ''}
                    onChange={(e) => handleFilterChange('active', e.target.value === '' ? '' : e.target.value === 'true')}
                    className="form-select"
                  >
                    <option value="">Todos los estados</option>
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="form-label invisible">Acciones</label>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ search: '', type: '', active: '' });
                      setPage(1);
                    }}
                    className="btn-secondary w-full"
                  >
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando clientes...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              <p>Error al cargar los clientes</p>
              <button 
                onClick={() => refetch()} 
                className="btn-secondary mt-2"
              >
                Reintentar
              </button>
            </div>
          ) : !customersData?.customers.length ? (
            <div className="p-8 text-center text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No se encontraron clientes</p>
              {canCreate && (
                <button
                  onClick={() => {
                    setSelectedCustomer(null);
                    setShowAddModal(true);
                  }}
                  className="btn-primary mt-4"
                >
                  Crear primer cliente
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Customer Table - Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Cliente</th>
                      <th className="table-header-cell">Tipo</th>
                      <th className="table-header-cell">Documento</th>
                      <th className="table-header-cell">Contacto</th>
                      <th className="table-header-cell">Estado</th>
                      <th className="table-header-cell">Fecha</th>
                      {canManage && (
                        <th className="table-header-cell text-right">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {customersData.customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {customer.name}
                            </div>
                            {customer.email && (
                              <div className="text-sm text-gray-500">
                                {customer.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            {getCustomerTypeIcon(customer.type)}
                            <span className="text-sm text-gray-900">
                              {customer.type === 'NATURAL' ? 'Natural' : 'Jurídica'}
                            </span>
                          </div>
                        </td>
                        <td className="table-cell">{customer.doc_number}</td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {customer.phone || 'N/A'}
                          </div>
                          {customer.address && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {customer.address}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={customer.active ? 'badge-success' : 'badge-danger'}>
                            {customer.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="table-cell text-gray-500">
                          {formatDate(customer.created_at)}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              {canEdit && (
                                <button
                                  onClick={() => {
                                    setSelectedCustomer(customer);
                                    setShowAddModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  onClick={() => handleToggleStatus(customer)}
                                  className={`p-1 ${customer.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                                  title={customer.active ? 'Desactivar' : 'Activar'}
                                >
                                  {customer.active ? (
                                    <ToggleRight className="h-4 w-4" />
                                  ) : (
                                    <ToggleLeft className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteCustomer(customer)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Customer Cards - Mobile */}
              <div className="lg:hidden space-y-4 p-4">
                {customersData.customers.map((customer) => (
                  <div key={customer.id} className="card">
                    <div className="card-body">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getCustomerTypeIcon(customer.type)}
                            <h3 className="text-lg font-medium text-gray-900">
                              {customer.name}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500">
                            {customer.type === 'NATURAL' ? 'Persona Natural' : 'Persona Jurídica'}
                          </p>
                        </div>
                        <span className={customer.active ? 'badge-success' : 'badge-danger'}>
                          {customer.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Documento</p>
                          <p className="font-medium text-gray-900">{customer.doc_number}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Teléfono</p>
                          <p className="font-medium text-gray-900">{customer.phone || 'N/A'}</p>
                        </div>
                        {customer.email && (
                          <div className="col-span-2">
                            <p className="text-gray-500">Email</p>
                            <p className="font-medium text-gray-900 break-all">{customer.email}</p>
                          </div>
                        )}
                        {customer.address && (
                          <div className="col-span-2">
                            <p className="text-gray-500">Dirección</p>
                            <p className="font-medium text-gray-900">{customer.address}</p>
                          </div>
                        )}
                        <div className="col-span-2">
                          <p className="text-gray-500">Fecha de registro</p>
                          <p className="font-medium text-gray-900">{formatDate(customer.created_at)}</p>
                        </div>
                      </div>

                      {canManage && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                          {canEdit && (
                            <button
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setShowAddModal(true);
                              }}
                              className="btn-sm btn-outline flex items-center gap-2 flex-1"
                            >
                              <Edit className="h-4 w-4" />
                              Editar
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => handleToggleStatus(customer)}
                              className={`btn-sm flex items-center gap-2 flex-1 ${
                                customer.active 
                                  ? 'btn-warning' 
                                  : 'btn-success'
                              }`}
                            >
                              {customer.active ? (
                                <>
                                  <ToggleRight className="h-4 w-4" />
                                  Desactivar
                                </>
                              ) : (
                                <>
                                  <ToggleLeft className="h-4 w-4" />
                                  Activar
                                </>
                              )}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteCustomer(customer)}
                              className="btn-sm btn-danger flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {customersData.pagination.pages > 1 && (
                <div className="px-4 sm:px-6 py-3 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                      Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, customersData.pagination.total)} de {customersData.pagination.total} clientes
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Anterior</span>
                        <span className="sm:hidden">‹</span>
                      </button>
                      <span className="text-sm text-gray-700 px-2">
                        <span className="hidden sm:inline">Página </span>{page} / {customersData.pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= customersData.pagination.pages}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Siguiente</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Client Modal */}
        <AddClientModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setSelectedCustomer(null);
          }}
          customer={selectedCustomer || undefined}
        />
      </div>
    </div>
  );
};

export default Customers;